/**
 * Secrets kept for agents' tools: names and facts, never values. The organisation's and its
 * teams' are an administrator's; a project's and its repositories' are the project's owners';
 * a person's own ("Yours") are theirs alone. Values leave the platform only when released to a
 * signed-in machine for a member. A person's own value sealed on their workbench (D177) says how
 * many devices can open it; "last used" names the person, and its keepers can open who used it.
 *
 * A list group (Layout.tsx): one row per secret with its state and one menu (Rotate, Disable,
 * Who used it, Remove); adding one and rotating one are sheets.
 */

import { SECRET_SCOPE_TEXT, SECRET_SCOPES, SECRET_USE_TEXT, SECRET_USES, secretIsStale, type SecretDraft, type SecretScope, type SecretUse, type SecretView } from '@aioe/shared';
import { Key } from '@gravity-ui/icons';
import { useState } from 'react';

import { ApiError, problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad, useNow } from '../hooks.ts';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark, Tick } from './ui.tsx';
import { VIEW_TONES, type ArtTone } from './ui/Art.tsx';
import { FormField, FormSheet } from './ui/FormSheet.tsx';
import { SearchBox } from './ui/SearchBox.tsx';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

interface Where {
  /** The project the list is read for; absent for the organisation's view. */
  projectId?: string;
  roots?: string[];
  teams?: Array<{ id: string; name: string }>;
}

export function SecretsPanel({ where, tone = VIEW_TONES.secrets! }: { where: Where; tone?: ArtTone }) {
  // Searched and paged on the server; the 60-second poll refreshes the first page only.
  const search = useListSearch();
  const list = usePagedList((cursor) => routes.secretsPage({ projectId: where.projectId, q: search.q, cursor }), { projectId: where.projectId, q: search.q }, { intervalMs: 60_000, keyOf: (s) => s.id });
  const data = { data: list.loaded ? { secrets: list.items, manage: list.extra?.manage ?? [] } : undefined, error: list.error, reload: list.reload };
  // Whether a secret is due for rotation moves on with the shared clock.
  const now = useNow();
  // Team names for team-scoped secrets, when the caller did not bring them (a member may not be allowed the list: then the id is shown).
  const teamList = useLoad(() => (where.teams ? Promise.resolve(where.teams) : routes.teams().then((ts) => ts.map((t) => ({ id: t.id, name: t.name }))).catch(() => [] as Array<{ id: string; name: string }>)), [where.projectId]);
  const teams = where.teams ?? teamList.data ?? [];
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const [removing, setRemoving] = useState<SecretView>();
  const [rotating, setRotating] = useState<SecretView>();
  const secrets = data.data?.secrets ?? [];
  const manage = data.data?.manage ?? [];
  const canAdd = manage.length > 0;
  const act = async (id: string, work: () => Promise<unknown>) => {
    setBusy(id);
    setError(undefined);
    try {
      await work();
      data.reload();
    } catch (e) {
      setError(((e as { body?: { message?: string } }).body?.message) ?? (e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const [usageOf, setUsageOf] = useState<string>();
  const place = (s: SecretView) => (s.scope === 'team' ? (teams.find((t) => t.id === s.teamId)?.name ?? `Team ${s.teamId}`) : s.scope === 'repository' ? `Repository ${s.rootId}` : SECRET_SCOPE_TEXT[s.scope]);
  const due = secrets.filter((s) => !s.disabled && manage.includes(s.scope) && secretIsStale(s, now)).length;
  const addButton = (
    <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>
      Add a secret
    </Button>
  );
  return (
    <>
      <ListGroup
        title="Secrets"
        count={data.data ? listCount(secrets.length, { one: 'secret', many: 'secrets' }, { total: search.q ? undefined : list.extra?.total, hasMore: list.hasMore, searched: search.q !== '' }) : undefined}
        description={where.projectId ? 'What this project’s agents may use. The most specific scope wins when two share a name.' : 'Kept by name; a value is never shown again once it is in.'}
        actions={
          <>
            {(list.extra?.total ?? 0) > 6 || search.q ? <SearchBox label="Search secrets" value={search.typed} onChange={search.setTyped} placeholder="Search secrets" className="w-40 max-w-full @lg:w-56" /> : null}
            {canAdd && (secrets.length > 0 || search.q) ? addButton : undefined}
          </>
        }
        empty={
          data.data && secrets.length === 0 && search.q ? (
            <NoMatch tone={tone} icon={<Key />} q={search.q} noun="secrets" onClear={search.clear} />
          ) : data.data && secrets.length === 0 ? (
            <Empty tone={tone} icon={<Key />} title="No secrets kept" body={canAdd ? 'Add an API key or a token by name. Machines ask for it when a session starts; the agent is given a stand-in and never the value.' : 'Secrets this project may use will show here by name.'} action={canAdd ? addButton : undefined} />
          ) : undefined
        }
      >
        {!data.data ? <ListBlock className="text-sm text-muted">{data.error ? <ErrorNote error={data.error} /> : 'Reading the secrets…'}</ListBlock> : null}
        {error || (data.data && data.error) ? (
          <ListBlock>
            <ErrorNote error={error ?? data.error} />
          </ListBlock>
        ) : null}
        {due ? (
          <ListBlock>
            <StatusMark state="medium" label={`${due} ${due === 1 ? 'secret you keep is' : 'secrets you keep are'} due for rotation`} />
          </ListBlock>
        ) : null}
        {secrets.map((s) => {
          const mine = manage.includes(s.scope);
          const stale = secretIsStale(s, now);
          const facts = factsLine(s);
          return (
            <ListRow
              key={s.id}
              title={<span className="mono">{s.name}</span>}
              description={
                <>
                  <span className="block">
                    {place(s)}
                    {s.description ? ` · ${s.description}` : ''}
                  </span>
                  <span className="block">
                    Used by {s.use.map((u) => SECRET_USE_TEXT[u].toLowerCase()).join(', ')}
                    {s.provider ? ` (${s.provider})` : ''} · version {s.version} · changed {timeAgo(s.rotatedAt)}
                    {s.lastUsedAt ? ` · last used ${timeAgo(s.lastUsedAt)}${s.lastUsedByName ? ` by ${s.lastUsedByName}` : ''}${s.useCount ? `, ${s.useCount} ${s.useCount === 1 ? 'time' : 'times'} in all` : ''}` : s.lastReleasedAt ? ` · released ${timeAgo(s.lastReleasedAt)}` : ' · never released'}
                  </span>
                  {facts ? <span className="block">{facts}</span> : null}
                </>
              }
              status={s.disabled ? <StatusMark state="disabled" label="Disabled" /> : stale ? <StatusMark state="medium" label="Due for rotation" /> : undefined}
              compactControl
              control={
                mine ? (
                  <RowMenu
                    label={`More for ${s.name}`}
                    items={[
                      { id: 'rotate', label: 'Rotate the value', disabled: !!busy },
                      !!s.lastUsedAt && { id: 'usage', label: usageOf === s.id ? 'Hide who used it' : 'Who used it' },
                      { id: 'toggle', label: s.disabled ? 'Enable' : 'Disable', disabled: !!busy },
                      { id: 'remove', label: 'Remove', danger: true, disabled: !!busy },
                    ]}
                    onAction={(id) => {
                      if (id === 'rotate') setRotating(s);
                      else if (id === 'usage') setUsageOf(usageOf === s.id ? undefined : s.id);
                      else if (id === 'toggle') void act(s.id, () => routes.changeSecret(s.id, { disabled: !s.disabled }));
                      else if (id === 'remove') setRemoving(s);
                    }}
                  />
                ) : undefined
              }
            >
              {usageOf === s.id ? <UsageRows id={s.id} /> : null}
            </ListRow>
          );
        })}
        {list.hasMore ? <ShowMore onPress={list.showMore} pending={list.loadingMore} /> : null}
      </ListGroup>
      {/* Mounted when opened, so its first scope is chosen from what the person may keep. */}
      {adding ? <AddSecret isOpen where={{ ...where, teams }} manage={manage} onDone={() => (setAdding(false), data.reload())} onCancel={() => setAdding(false)} /> : null}
      <RotateSecret secret={rotating} onDone={() => (setRotating(undefined), data.reload())} onCancel={() => setRotating(undefined)} />
      <ConfirmDialog
        isOpen={!!removing}
        title={`Remove the secret ${removing?.name ?? ''}`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          const s = removing;
          setRemoving(undefined);
          if (s) await act(s.id, () => routes.removeSecret(s.id));
        }}
      >
        Machines lose it at their next ask.
      </ConfirmDialog>
    </>
  );
}

/** Whether a refusal is the one for a plain value where the person's devices seal their own (`seal_expected`). */
function sealExpected(e: unknown): boolean {
  return e instanceof ApiError && (e.body as { error?: string } | undefined)?.error === 'seal_expected';
}

/** Who used a secret, one row per person (each machine reports under the person it works for), most recent first. */
function UsageRows({ id }: { id: string }) {
  const data = useLoad(() => routes.secretUsage(id), [id]);
  if (data.error) return <ErrorNote error={data.error} />;
  if (!data.data) return <div className="text-xs text-muted">Reading who used it…</div>;
  if (data.data.usage.length === 0) return <div className="text-xs text-muted">No machine has said who used it yet.</div>;
  return (
    <ul className="grid gap-1 border-s-2 border-separator ps-3 text-xs">
      {data.data.usage.map((u) => (
        <li key={u.principal} className="flex flex-wrap items-baseline justify-between gap-x-4">
          <span className="min-w-0 truncate font-medium">{u.name ?? u.principal}</span>
          <span className="text-muted tabular-nums">
            {u.count} {u.count === 1 ? 'use' : 'uses'} · last {timeAgo(u.lastAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** What a secret says about who else may stand in for it and where it may go, in one sentence; nothing when it says nothing. */
function factsLine(s: SecretView): string | undefined {
  const parts: string[] = [];
  if (s.sealedFor) parts.push(`sealed for ${s.sealedFor} ${s.sealedFor === 1 ? 'device' : 'devices'}: only ${s.scope === 'personal' ? 'yours' : 'those'} can open it`);
  if (s.locked) parts.push('Locked: nothing else of this name is used');
  if (s.personalOverride === 'allow') parts.push('people may use their own instead');
  if (s.scope === 'personal') parts.push(s.follow ? 'follows you to nodes that run your work' : 'only on your own computers');
  if (s.allowedHosts?.length) parts.push(`sent only to ${s.allowedHosts.join(', ')}`);
  if (!parts.length) return undefined;
  const text = parts.join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A person's own value came in plain while their devices seal their own: it is kept so only when they say so. */
function SealNote({ note, saving, onKeep }: { note: string; saving: boolean; onKeep: () => void }) {
  return (
    <div role="status" className="grid gap-2 rounded-xl bg-surface-secondary px-3 py-2.5 text-sm">
      <span>{note}</span>
      <span className="text-xs text-muted">Kept unsealed, the organisation can open it when it releases it to your machines, as for shared secrets.</span>
      <div>
        <Button size="sm" variant="secondary" isPending={saving} onPress={onKeep}>
          Keep it unsealed
        </Button>
      </div>
    </div>
  );
}

/** A new value for a secret, in a sheet: sealed on the way in and never shown again. */
function RotateSecret({ secret, onDone, onCancel }: { secret?: SecretView; onDone: () => void; onCancel: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const [sealNote, setSealNote] = useState<string>();
  const [saving, setSaving] = useState(false);
  const close = (done: boolean) => {
    setValue('');
    setError(undefined);
    setSealNote(undefined);
    if (done) onDone();
    else onCancel();
  };
  const save = async (plain = false) => {
    if (!secret) return;
    setSaving(true);
    setError(undefined);
    try {
      await routes.rotateSecret(secret.id, value, plain ? { plain: true } : {});
      close(true);
    } catch (e) {
      // A person's own value, refused because their devices seal it: kept unsealed only when they say so.
      if (sealExpected(e)) setSealNote(problemText(e));
      else setError(problemText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormSheet
      isOpen={!!secret}
      width="md"
      title={`Rotate ${secret?.name ?? 'the secret'}`}
      description="The new value is sealed on the way in and never shown again. Machines take it at their next ask."
      onClose={() => close(false)}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={() => close(false)}>
            Cancel
          </Button>
          {!sealNote ? (
            <Button size="sm" variant="primary" isPending={saving} isDisabled={!value} onPress={() => void save()}>
              Rotate
            </Button>
          ) : null}
        </>
      }
    >
      <FormField label="New value">
        <input
          type="password"
          className={`${inputClass} mono`}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSealNote(undefined);
          }}
          autoComplete="new-password"
          autoFocus
        />
      </FormField>
      <ErrorNote error={error} />
      {sealNote ? <SealNote note={sealNote} saving={saving} onKeep={() => void save(true)} /> : null}
    </FormSheet>
  );
}

function AddSecret({ isOpen, where, manage, onDone, onCancel }: { isOpen: boolean; where: Where; manage: SecretScope[]; onDone: () => void; onCancel: () => void }) {
  const scopes = SECRET_SCOPES.filter((s) => manage.includes(s) && (s === 'organisation' || s === 'personal' || (s === 'team' ? (where.teams?.length ?? 0) > 0 : !!where.projectId) && (s !== 'repository' || (where.roots?.length ?? 0) > 0)));
  const first = (['project', 'organisation', 'team', 'repository', 'personal'] as SecretScope[]).find((x) => scopes.includes(x)) ?? 'personal';
  const blank = () => ({ scope: first, teamId: where.teams?.[0]?.id ?? '', rootId: where.roots?.[0] ?? '', name: '', value: '', description: '', use: ['tools'] as SecretUse[], provider: '', rotateAfterDays: '', locked: false, personalOverride: false, follow: false, hosts: '' });
  const [d, setD] = useState(blank);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  /** The person's devices seal their own secrets: this value came in plain, and may be kept so only on purpose. */
  const [sealNote, setSealNote] = useState<string>();
  const set = (patch: Partial<typeof d>) => (setD((x) => ({ ...x, ...patch })), setSealNote(undefined));
  const close = (done: boolean) => {
    setD(blank());
    setError(undefined);
    setSealNote(undefined);
    if (done) onDone();
    else onCancel();
  };
  const save = async (plain = false) => {
    setSaving(true);
    setError(undefined);
    const draft: SecretDraft = {
      scope: d.scope,
      ...(d.scope === 'team' ? { teamId: d.teamId } : {}),
      ...(d.scope === 'project' || d.scope === 'repository' ? { projectId: where.projectId! } : {}),
      ...(d.scope === 'repository' ? { rootId: d.rootId } : {}),
      name: d.name.trim(),
      value: d.value,
      use: d.use,
      ...(d.description.trim() ? { description: d.description.trim() } : {}),
      ...(d.use.includes('provider') && d.provider.trim() ? { provider: d.provider.trim() } : {}),
      ...(d.rotateAfterDays ? { rotateAfterDays: Number(d.rotateAfterDays) } : {}),
      ...((d.scope === 'organisation' || d.scope === 'team') && d.locked ? { locked: true } : {}),
      ...(d.scope !== 'personal' && d.personalOverride ? { personalOverride: 'allow' as const } : {}),
      ...(d.scope === 'personal' && d.follow ? { follow: true } : {}),
      ...(d.use.includes('tools') && d.hosts.trim() ? { allowedHosts: d.hosts.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean) } : {}),
    };
    try {
      await routes.createSecret(draft, { plain });
      close(true);
    } catch (e) {
      if (sealExpected(e)) {
        setSealNote(problemText(e));
        return;
      }
      const body = (e as { body?: { message?: string; issues?: Array<{ message: string }> } }).body;
      setError(body?.message ?? body?.issues?.[0]?.message ?? (e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const sealing = !!sealNote && d.scope === 'personal';
  return (
    <FormSheet
      isOpen={isOpen}
      width="xl"
      title="Add a secret"
      description="Kept by name. Machines ask for it when a session starts; the agent is given a stand-in and never the value."
      onClose={() => close(false)}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={() => close(false)}>
            Cancel
          </Button>
          {!sealing ? (
            <Button size="sm" variant="primary" isPending={saving} isDisabled={!d.name.trim() || !d.value || d.use.length === 0} onPress={() => void save()}>
              Keep it
            </Button>
          ) : null}
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Name, as the tool expects it">
          <input className={`${inputClass} mono`} value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="TRACKER_TOKEN" autoComplete="off" />
        </FormField>
        <FormField label="Value" hint="Sealed on the way in, never shown again.">
          <input type="password" className={`${inputClass} mono`} value={d.value} onChange={(e) => set({ value: e.target.value })} autoComplete="new-password" />
        </FormField>
        <FormField label="Kept by">
          <ChoiceSelect label="Kept by" className="w-full" value={d.scope} options={Object.fromEntries(scopes.map((s) => [s, SECRET_SCOPE_TEXT[s]])) as Record<SecretScope, string>} onChange={(v) => set({ scope: v })} />
        </FormField>
        {d.scope === 'team' && (
          <FormField label="Team">
            <ChoiceSelect label="Team" className="w-full" value={d.teamId || undefined} options={Object.fromEntries((where.teams ?? []).map((t) => [t.id, t.name]))} onChange={(v) => set({ teamId: v })} />
          </FormField>
        )}
        {d.scope === 'repository' && (
          <FormField label="Repository">
            <ChoiceSelect label="Repository" className="w-full" value={d.rootId || undefined} options={Object.fromEntries((where.roots ?? []).map((r) => [r, r]))} onChange={(v) => set({ rootId: v })} />
          </FormField>
        )}
        <FormField label="What it is for" className="sm:col-span-2">
          <input className={inputClass} value={d.description} onChange={(e) => set({ description: e.target.value })} placeholder="The tracker's service token." />
        </FormField>
        <FormField label="Remind us to rotate it after" hint="Days. Leave empty for no reminder.">
          <input type="number" min={1} max={3650} className={`${inputClass} tabular-nums`} value={d.rotateAfterDays} onChange={(e) => set({ rotateAfterDays: e.target.value })} placeholder="90" />
        </FormField>
      </div>
      <fieldset className="grid gap-1.5">
        <legend className="mb-1.5 text-xs font-medium text-foreground">Used by</legend>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {SECRET_USES.map((u) => (
            <Tick key={u} checked={d.use.includes(u)} onChange={(v) => set({ use: v ? [...d.use, u] : d.use.filter((x) => x !== u) })}>
              {SECRET_USE_TEXT[u]}
            </Tick>
          ))}
          {d.use.includes('provider') && <input className={`${inputClass} w-40`} value={d.provider} onChange={(e) => set({ provider: e.target.value })} placeholder="anthropic" aria-label="Provider" />}
        </div>
        <span className="text-xs text-muted">Tools: the workbench’s gate holds it and the agent never sees it. A model provider: the inference proxy holds it. The agent’s environment: the only one an agent can read.</span>
      </fieldset>
      {d.use.includes('tools') && (
        <FormField label="Only send it to (optional)" hint="A tool server for any other address is refused the value, whatever a repository’s configuration says.">
          <input className={`${inputClass} mono`} value={d.hosts} onChange={(e) => set({ hosts: e.target.value })} placeholder="api.github.com, *.atlassian.net" />
        </FormField>
      )}
      <div className="grid gap-1.5 text-sm">
        {(d.scope === 'organisation' || d.scope === 'team') && (
          <Tick checked={d.locked} onChange={(v) => set({ locked: v })}>
            Locked: a project, repository or person cannot use another value of this name
          </Tick>
        )}
        {d.scope !== 'personal' && !d.locked && (
          <Tick checked={d.personalOverride} onChange={(v) => set({ personalOverride: v })}>
            People may use their own value of this name instead
          </Tick>
        )}
        {d.scope === 'personal' && (
          <Tick checked={d.follow} onChange={(v) => set({ follow: v })}>
            Follow me to the nodes I let run my work (otherwise only my own computers)
          </Tick>
        )}
      </div>
      <ErrorNote error={error} />
      {sealing ? <SealNote note={sealNote} saving={saving} onKeep={() => void save(true)} /> : null}
    </FormSheet>
  );
}
